import { useStore } from 'vuex'
import { computed, getCurrentInstance } from 'vue'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { SchemaType } from '@/constants/schema'

const useDomainEventActions = () => {
  const store = useStore()
  const { proxy } = getCurrentInstance()
  const workflow = computed(() => store.state.workflow.workflow)

  const blurDomainEventName = async (e, schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const newName = e.target.innerText.trim()
    if (newSchemas[schemaIndex].name === newName) {
      return
    }

    const currentSchema = newSchemas[schemaIndex]
    const hasBoundedContext = !!currentSchema.boundedContext
    const isDuplicate = hasBoundedContext && newSchemas.some((s) =>
      s.id !== schemaId &&
      s.type === SchemaType.DOMAIN_EVENT &&
      s.boundedContext === currentSchema.boundedContext &&
      s.name.toLowerCase() === newName.toLowerCase()
    )

    if (isDuplicate) {
      e.target.innerText = currentSchema.name
      const bcName = (workflow.value.boundedContexts || []).find((bc) => bc.id === currentSchema.boundedContext)?.name || 'this bounded context'
      proxy.$store.commit('snackbar/showMessage', {
        content: `A domain event with the name "${newName}" already exists in ${bcName}`,
        timeout: 4000,
        color: 'red'
      }, { root: true })
      return
    }

    newSchemas[schemaIndex].name = newName
    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const deleteDomainEvent = async (schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas.splice(schemaIndex, 1)

    // Unlink any cards that reference this schema
    const svgGraph = store.getters['svgGraphData/svgGraphData']
    const updatedSvgGraph = safeStructuredClone(svgGraph)
    let svgGraphChanged = false

    Object.keys(updatedSvgGraph).forEach((eventId) => {
      const eventData = updatedSvgGraph[eventId]
      if (eventData.cards) {
        eventData.cards = eventData.cards.map((card) => {
          if (card.schemaId === schemaId) {
            svgGraphChanged = true
            return { ...card, schemaId: null }
          }
          return card
        })
      }
    })

    await workflowApi.updateWorkflow({
      svgGraph: svgGraphChanged ? updatedSvgGraph : undefined,
      schemas: newSchemas,
      workflow: workflow.value,
      updateNote: {
        action: 'deleted domain event',
        target: ''
      }
    }, true)
  }

  return {
    blurDomainEventName,
    deleteDomainEvent
  }
}

export default useDomainEventActions
